import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RatingGroup } from "#app/components/tables/Funnel/types";
import { ProcessedChartDataDTO } from "#app/api/dto/funnel/funnelChartDTO";

export interface UserTableClientDTO {
    id: number;
    name: string;
    sumServices: number;
    sumActives: number;
    sumServicesPercent?: number;
    sumActivesPercent?: number;
}

export interface ClientActiveDTO {
    user_id: string | number;
    valuation?: number;
    type_id?: number;
    name_text?: string;
    account_name?: string;
    [key: string]: any;
}

export interface FunnelDataState {
    ratingGroups: RatingGroup[];
    ratingGroupsOriginal: RatingGroup[];
    ratingGroupsLoading: boolean;
    ratingGroupsError: string | null;
    ratingGroupsLastUpdated: number | null;

    userTableClients: UserTableClientDTO[];
    userTableClientsOriginal: UserTableClientDTO[];
    userTableLoading: boolean;
    userTableError: string | null;
    userTableLastUpdated: number | null;

    chartData: ProcessedChartDataDTO | null;
    chartDataOriginal: ProcessedChartDataDTO | null;
    chartDataLoading: boolean;
    chartDataError: string | null;
    chartDataLastUpdated: number | null;

    activesData: ClientActiveDTO[];
    activesDataOriginal: ClientActiveDTO[];
    activesDataLoading: boolean;
    activesDataError: string | null;
    activesDataLastUpdated: number | null;

    currentManagerId: number | null;
    currentAtonValuation: number | null;
    currentSelectedRating: number | null;
    currentSelectedClientIds: number[];
    clientsCount: number;
}

const initialState: FunnelDataState = {
    ratingGroups: [],
    ratingGroupsOriginal: [],
    ratingGroupsLoading: false,
    ratingGroupsError: null,
    ratingGroupsLastUpdated: null,

    userTableClients: [],
    userTableClientsOriginal: [],
    userTableLoading: false,
    userTableError: null,
    userTableLastUpdated: null,

    chartData: null,
    chartDataOriginal: null,
    chartDataLoading: false,
    chartDataError: null,
    chartDataLastUpdated: null,

    activesData: [],
    activesDataOriginal: [],
    activesDataLoading: false,
    activesDataError: null,
    activesDataLastUpdated: null,

    currentManagerId: null,
    currentAtonValuation: null,
    currentSelectedRating: null,
    currentSelectedClientIds: [],
    clientsCount: 0,
};

/**
 *
 * @param firstValue
 * @param secondValue
 */
const deepEqual = (firstValue: any, secondValue: any): boolean => {
    if (firstValue === secondValue) return true;
    if (firstValue == null || secondValue == null) return false;
    if (typeof firstValue !== typeof secondValue) return false;

    if (Array.isArray(firstValue) && Array.isArray(secondValue)) {
        if (firstValue.length !== secondValue.length) return false;
        return firstValue.every((item, index) => deepEqual(item, secondValue[index]));
    }

    if (typeof firstValue === "object") {
        const firstKeys = Object.keys(firstValue);
        const secondKeys = Object.keys(secondValue);
        if (firstKeys.length !== secondKeys.length) return false;
        return firstKeys.every((key) => deepEqual(firstValue[key], secondValue[key]));
    }

    return false;
};

/**
 *
 * @param firstGroups
 * @param secondGroups
 */
const compareRatingGroups = (firstGroups: RatingGroup[], secondGroups: RatingGroup[]): boolean => {
    if (firstGroups.length !== secondGroups.length) return false;

    return firstGroups.every((groupA, index) => {
        const groupB = secondGroups[index];
        return (
            groupA.id === groupB.id &&
            groupA.rating === groupB.rating &&
            groupA.countClients === groupB.countClients &&
            groupA.sumActives === groupB.sumActives &&
            groupA.sumServices === groupB.sumServices &&
            (groupA.countClientsPercent || 0) === (groupB.countClientsPercent || 0) &&
            (groupA.sumActivesPercent || 0) === (groupB.sumActivesPercent || 0) &&
            (groupA.sumServicesPercent || 0) === (groupB.sumServicesPercent || 0)
        );
    });
};

/**
 *
 * @param firstClients
 * @param secondClients
 */
const compareUserTableClients = (
    firstClients: UserTableClientDTO[],
    secondClients: UserTableClientDTO[]
): boolean => {
    if (firstClients.length !== secondClients.length) return false;

    return firstClients.every((clientA, index) => {
        const clientB = secondClients[index];
        return (
            clientA.id === clientB.id &&
            clientA.name === clientB.name &&
            clientA.sumServices === clientB.sumServices &&
            clientA.sumActives === clientB.sumActives &&
            (clientA.sumServicesPercent || 0) === (clientB.sumServicesPercent || 0) &&
            (clientA.sumActivesPercent || 0) === (clientB.sumActivesPercent || 0)
        );
    });
};

export const funnelDataSlice = createSlice({
    name: "funnelData",
    initialState,
    reducers: {
        resetFunnelDataState: (state) => {
            Object.assign(state, initialState);
        },
        setRatingGroups: (state, action: PayloadAction<RatingGroup[]>) => {
            state.ratingGroups = action.payload;
            state.ratingGroupsLastUpdated = Date.now();
            state.ratingGroupsError = null;
        },
        setRatingGroupsOriginal: (state, action: PayloadAction<RatingGroup[]>) => {
            state.ratingGroupsOriginal = JSON.parse(JSON.stringify(action.payload));
            state.ratingGroups = JSON.parse(JSON.stringify(action.payload));
            state.ratingGroupsLastUpdated = Date.now();
        },

        restoreRatingGroupsFromOriginal: (state) => {
            if (state.ratingGroupsOriginal.length > 0) {
                state.ratingGroups = JSON.parse(JSON.stringify(state.ratingGroupsOriginal));
                state.ratingGroupsLastUpdated = Date.now();
                state.ratingGroupsError = null;
            }
        },
        setRatingGroupsLoading: (state, action: PayloadAction<boolean>) => {
            state.ratingGroupsLoading = action.payload;
        },
        setRatingGroupsError: (state, action: PayloadAction<string | null>) => {
            state.ratingGroupsError = action.payload;
            state.ratingGroupsLoading = false;
        },

        setUserTableClients: (state, action: PayloadAction<UserTableClientDTO[]>) => {
            if (!compareUserTableClients(state.userTableClients, action.payload)) {
                state.userTableClients = action.payload;
                state.userTableClientsOriginal = JSON.parse(JSON.stringify(action.payload));
                state.userTableLastUpdated = Date.now();
                state.userTableError = null;
            }
        },
        restoreUserTableClients: (state) => {
            if (state.userTableClientsOriginal.length > 0) {
                state.userTableClients = JSON.parse(JSON.stringify(state.userTableClientsOriginal));
                state.userTableLastUpdated = Date.now();
            }
        },
        setUserTableLoading: (state, action: PayloadAction<boolean>) => {
            state.userTableLoading = action.payload;
        },
        setUserTableError: (state, action: PayloadAction<string | null>) => {
            state.userTableError = action.payload;
            state.userTableLoading = false;
        },

        setChartData: (state, action: PayloadAction<ProcessedChartDataDTO | null>) => {
            const newData = action.payload;
            if (!newData) {
                state.chartData = null;
                state.chartDataOriginal = null;
                state.chartDataLastUpdated = Date.now();
                return;
            }

            const oldData = state.chartData;
            if (!oldData) {
                state.chartData = newData;
                state.chartDataOriginal = JSON.parse(JSON.stringify(newData));
                state.chartDataLastUpdated = Date.now();
                state.chartDataError = null;
                return;
            }

            const dataChanged =
                oldData.minDate.valueOf() !== newData.minDate.valueOf() ||
                oldData.maxDate.valueOf() !== newData.maxDate.valueOf() ||
                oldData.filledData.length !== newData.filledData.length ||
                oldData.clientsData.length !== newData.clientsData.length;

            if (dataChanged) {
                state.chartData = newData;
                state.chartDataOriginal = JSON.parse(JSON.stringify(newData));
                state.chartDataLastUpdated = Date.now();
                state.chartDataError = null;
            }
        },
        restoreChartData: (state) => {
            if (state.chartDataOriginal) {
                state.chartData = JSON.parse(JSON.stringify(state.chartDataOriginal));
                state.chartDataLastUpdated = Date.now();
            }
        },
        setChartDataLoading: (state, action: PayloadAction<boolean>) => {
            state.chartDataLoading = action.payload;
        },
        setChartDataError: (state, action: PayloadAction<string | null>) => {
            state.chartDataError = action.payload;
            state.chartDataLoading = false;
        },

        setActivesData: (state, action: PayloadAction<ClientActiveDTO[]>) => {
            if (!deepEqual(state.activesData, action.payload)) {
                state.activesData = action.payload;
                state.activesDataOriginal = JSON.parse(JSON.stringify(action.payload));
                state.activesDataLastUpdated = Date.now();
                state.activesDataError = null;
            }
        },
        restoreActivesData: (state) => {
            if (state.activesDataOriginal.length > 0) {
                state.activesData = JSON.parse(JSON.stringify(state.activesDataOriginal));
                state.activesDataLastUpdated = Date.now();
            }
        },
        setActivesDataLoading: (state, action: PayloadAction<boolean>) => {
            state.activesDataLoading = action.payload;
        },
        setActivesDataError: (state, action: PayloadAction<string | null>) => {
            state.activesDataError = action.payload;
            state.activesDataLoading = false;
        },

        setFunnelParams: (
            state,
            action: PayloadAction<{
                managerId?: number | null;
                atonValuation?: number | null;
                selectedRating?: number | null;
                selectedClientIds?: number[];
                clientsCount?: number;
            }>
        ) => {
            const { managerId, atonValuation, selectedRating, selectedClientIds, clientsCount } =
                action.payload;
            if (managerId !== undefined) state.currentManagerId = managerId;
            if (atonValuation !== undefined) state.currentAtonValuation = atonValuation;
            if (selectedRating !== undefined) state.currentSelectedRating = selectedRating;
            if (clientsCount !== undefined) state.clientsCount = clientsCount;
            if (selectedClientIds !== undefined) {
                if (!deepEqual(state.currentSelectedClientIds, selectedClientIds)) {
                    state.currentSelectedClientIds = selectedClientIds;
                }
            }
        },

        clearFunnelData: (state) => {
            state.ratingGroups = [];
            state.ratingGroupsOriginal = [];
            state.userTableClients = [];
            state.userTableClientsOriginal = [];
            state.chartData = null;
            state.chartDataOriginal = null;
            state.activesData = [];
            state.activesDataOriginal = [];
            state.ratingGroupsLastUpdated = null;
            state.userTableLastUpdated = null;
            state.chartDataLastUpdated = null;
            state.activesDataLastUpdated = null;
        },
    },
});

export const {
    resetFunnelDataState,
    setRatingGroups,
    setRatingGroupsOriginal,
    restoreRatingGroupsFromOriginal,
    setRatingGroupsLoading,
    setRatingGroupsError,
    setUserTableClients,
    restoreUserTableClients,
    setUserTableLoading,
    setUserTableError,
    setChartData,
    restoreChartData,
    setChartDataLoading,
    setChartDataError,
    setActivesData,
    restoreActivesData,
    setActivesDataLoading,
    setActivesDataError,
    setFunnelParams,
    clearFunnelData,
} = funnelDataSlice.actions;

export const selectFunnelDataState = (state: any): FunnelDataState =>
    state.funnelData || initialState;

export const selectRatingGroups = (state: any): RatingGroup[] =>
    selectFunnelDataState(state).ratingGroups;

export const selectRatingGroupsOriginal = (state: any): RatingGroup[] =>
    selectFunnelDataState(state).ratingGroupsOriginal;

export const selectRatingGroupsLoading = (state: any): boolean =>
    selectFunnelDataState(state).ratingGroupsLoading;

export const selectUserTableClients = (state: any): UserTableClientDTO[] =>
    selectFunnelDataState(state).userTableClients;

export const selectUserTableLoading = (state: any): boolean =>
    selectFunnelDataState(state).userTableLoading;

export const selectChartData = (state: any): ProcessedChartDataDTO | null =>
    selectFunnelDataState(state).chartData;

export const selectChartDataLoading = (state: any): boolean =>
    selectFunnelDataState(state).chartDataLoading;

export const selectActivesData = (state: any): ClientActiveDTO[] =>
    selectFunnelDataState(state).activesData;

export const selectFunnelParams = (state: any) => ({
    managerId: selectFunnelDataState(state).currentManagerId,
    atonValuation: selectFunnelDataState(state).currentAtonValuation,
    selectedRating: selectFunnelDataState(state).currentSelectedRating,
    selectedClientIds: selectFunnelDataState(state).currentSelectedClientIds,
});

export default funnelDataSlice.reducer;
